import { createElement } from 'react';

import BaseLink from '@/components/common/BaseLink/BaseLink';
import { Icon } from '@/components/common/Icon';
import { Heading, Paragraph } from '@/components/common/MarkUp';
import {
  determineDestination,
  PortableText,
  VisuallyHidden,
} from '@/components/common/utils';
import {
  ContentContainer,
  DefaultBlock,
  ListItem,
  StyledListElement,
} from '@/components/page/case-study/Body/Body.styles';
import {
  findMatchingListBlock,
  getElementComponent,
  getListComponent,
} from '@/components/page/case-study/caseStudyUtils';

import type { ListProps } from '@/components/common/MarkUp';
import type { SanityBlock, SanityKeyed } from '@/types/sanity';

export interface PostBodyProps {
  block: SanityKeyed<SanityBlock>;
  id: string;
  style: string;
  marks: Array<string>;
  list?: string;
  UlList?: Array<Array<SanityKeyed<SanityBlock>>>;
  OlList?: Array<Array<SanityKeyed<SanityBlock>>>;
}

export const PostBodyContent = ({
  block,
  style,
  marks,
  list,
  id,
  UlList,
  OlList,
}: PostBodyProps) => {
  const isStrong = marks && marks[0] === 'strong';
  const blockElement = findMatchingListBlock(block, OlList, UlList);
  const isList = blockElement?.length > 0;
  const listElement = getListComponent(isList, list, id);
  const element = getElement(style, isStrong, list, listElement);

  if (!element.component) {
    return null;
  }

  return (
    <ContentContainer>
      {createElement(
        element.component,
        element.props,
        <PortableText value={blockElement} components={getComponents()} />,
      )}
    </ContentContainer>
  );
};

const getElement = (style, isStrong, list, listElement) => {
  let element = getElementComponent(style, isStrong) || {
    component: null,
    props: {},
  };

  if (!element.component && !list) {
    element.component = DefaultBlock;
  }

  if (listElement) {
    element = listElement;
  }

  return element;
};

const renderBlock = (value, children) => {
  switch (value.style) {
    case 'normal':
      return <Paragraph size="body-lg">{children}</Paragraph>;
    case 'h2':
      return <Heading size="heading-lg">{children}</Heading>;
    case 'h4':
      return <Heading size="heading-sm">{children}</Heading>;
    default:
      return children;
  }
};

const renderLink = (value, children) => {
  const { href } = value;

  if (!href) {
    return <span>{children}</span>;
  }

  const destination = determineDestination(href);
  return (
    <BaseLink
      href={href}
      target={destination.target}
      rel={destination.isExternalLink ? 'external noopener noreferrer' : null}
    >
      {children}
      {destination.isExternalLink && (
        <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
      )}
    </BaseLink>
  );
};

const renderInternalLink = (value, children) => {
  let prefix = '';

  switch (value?.item?._type) {
    case 'blogPost':
      prefix = '/blog';
      break;
    case 'programme': {
      const { audienceRef, region } = value.item;

      const basePath = audienceRef ? audienceRef.basePath : '';
      const programmesPath = region === 'en-GB' ? 'programmes' : 'programs';

      prefix = `${basePath}/${programmesPath}`;
      break;
    }
    case 'page':
    default: {
      const audienceRef = value?.item?.audienceRef;
      prefix = audienceRef?.basePath ?? '';
      break;
    }
  }

  const href = `${prefix}/${value.item?.slug.current}`;

  return <BaseLink href={href}>{children}</BaseLink>;
};

const ListElement = ({ size, type = 'ul', children, ...props }: ListProps) => {
  return (
    <StyledListElement size={size} as={type} {...props}>
      <Icon icon="CheckCircle" colour="--icon-galaxy" size="20px" />
      {children}
    </StyledListElement>
  );
};

const getComponents = () => {
  const components = {
    block: ({ value, children }) => renderBlock(value, children),
    marks: {
      strong: ({ children }) => <b>{children}</b>,
      link: ({ value, children }) => renderLink(value, children),
      internalLink: ({ value, children }) =>
        renderInternalLink(value, children),
    },
    list: ({ value, children }) => (
      <ListElement
        size="body-lg"
        type={value.listItem === 'bullet' ? 'ul' : 'ol'}
      >
        {children}
      </ListElement>
    ),
    listItem: ({ children }) => <ListItem>{children}</ListItem>,
  };
  return components;
};
